import { Component, HostListener, OnInit } from '@angular/core';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BarnService } from '../../../services/barn.service';
import { PlayerService } from '../../../services/player.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { ApplicationService } from '../../../services/application.service';
import { Player } from '../../../models/Player';
import { BarnBooking } from '../../../models/barn';
import { BARN, BARN_BOOKING_KIND, PERMISSIONS } from '../../../models/constants';
import {
  addDays,
  buildSlotMinutes,
  formatTimeOfDay,
  getDayOfWeek,
  getWeekStartDate,
  getZonedParts,
  parseDateLiteral,
  getMinutesFromMidnight,
  toDateLiteral,
  toInstant,
  toTimeLiteral
} from '../../../util/barntime';

interface BarnDate {
  year:number,
  month:number,
  day:number
}

interface CalendarSlot {
  startMinute:number,
  startAt:Date,
  isBookable:boolean
}

interface CalendarEvent {
  booking:BarnBooking,
  topPercent:number,
  heightPercent:number,
  title:string,
  timeRange:string,
  isBlackout:boolean,
  isMine:boolean
}

interface CalendarDay {
  date:BarnDate,
  dayOfWeek:number,
  dayName:string,
  dayNumber:number,
  monthName:string,
  isToday:boolean,
  slots:Array<CalendarSlot>,
  events:Array<CalendarEvent>,
  openSlotCount:number
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

@Component({
  selector: 'app-barn-availability',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, FormsModule],
  templateUrl: './barn-availability.component.html',
  styleUrl: './barn-availability.component.css'
})
export class BarnAvailabilityComponent implements OnInit {
  constructor(
    private barnService:BarnService,
    private playerService:PlayerService,
    private authenticationService:AuthenticationService,
    private applicationService:ApplicationService) {}

  isTeeTimeAdmin:boolean = false;
  isNarrowScreen:boolean = false;
  currentPlayerId:number = 0;
  currentPlayerName:string = '';

  weekStart:BarnDate = getWeekStartDate(new Date());
  selectedDayIndex:number = 0;
  days:Array<CalendarDay> = [];
  bookings:Array<BarnBooking> = [];
  gridSlotMinutes:Array<number> = buildSlotMinutes(BARN.OPEN_MINUTE, BARN.CLOSE_MINUTE);
  roster:Array<Player> = [];
  errorMessage:string = '';
  noticeMessage:string = '';

  /* Modal state: RESERVE, DETAILS, BLACKOUT or empty for none */
  activeModal:string = '';
  modalError:string = '';

  reserveDay:CalendarDay|null = null;
  reserveSlot:CalendarSlot|null = null;
  reserveDurationOptions:Array<number> = [];
  reserveDuration:number = BARN.DEFAULT_DURATION_MINUTES;
  reserveSelectedPlayerIds:Array<number> = [];
  reserveGuestNames:Array<string> = [];

  detailsEvent:CalendarEvent|null = null;

  blackoutIsRecurring:boolean = false;
  blackoutLabel:string = '';
  blackoutDate:string = '';
  blackoutStartMinute:number = BARN.OPEN_MINUTE;
  blackoutEndMinute:number = BARN.CLOSE_MINUTE;
  blackoutDaysOfWeek:Array<number> = [];
  blackoutStartsOn:string = '';
  blackoutEndsOn:string = '';
  blackoutTimeOptions:Array<number> = buildSlotMinutes(BARN.OPEN_MINUTE, BARN.CLOSE_MINUTE + BARN.SLOT_MINUTES);
  dayOfWeekOptions = DAY_NAMES.map((name, index) => ({ name: name, value: index }));

  notificationSelectedPlayerIds:Array<number> = [];
  adminMenuOpen:boolean = false;
  activeView:'calendar'|'upcoming' = 'calendar';
  upcomingBookings:Array<BarnBooking> = [];
  upcomingError:string = '';

  async ngOnInit():Promise<void> {
    this.onResize();

    const player = this.authenticationService.loginResult?.player;
    if(player) {
      this.currentPlayerId = player.playerId;
      this.currentPlayerName = `${player.firstName} ${player.lastName}`;
    }

    this.isTeeTimeAdmin = await this.authenticationService.hasPermission(PERMISSIONS.TEETIMEADMIN);
    this.selectedDayIndex = this.getTodayIndexInWeek();

    this.applicationService.showGlobalSpinner();
    this.roster = await this.playerService.searchPlayers(null);
    await this.loadWeek();
    this.applicationService.hideGlobalSpinner();
  }

  @HostListener('window:resize')
  onResize():void {
    this.isNarrowScreen = window.innerWidth < 700;
  }

  @HostListener('document:click')
  onDocumentClick():void {
    this.adminMenuOpen = false;
  }

  /* ---------- calendar data ---------- */

  async loadWeek():Promise<void> {
    this.errorMessage = '';

    const rangeStart = toInstant(this.weekStart.year, this.weekStart.month, this.weekStart.day, 0);
    const weekEnd = addDays(this.weekStart, 7);
    const rangeEnd = toInstant(weekEnd.year, weekEnd.month, weekEnd.day, 0);

    try {
      this.bookings = await this.barnService.getAvailability(rangeStart, rangeEnd);
    }
    catch (err) {
      this.bookings = [];
      this.errorMessage = this.describeError(err);
    }

    this.buildDays();
  }

  buildDays():void {
    const now = new Date();
    const latestBookable = new Date(now.getTime() + (BARN.MAX_DAYS_AHEAD * 24 * 60 * 60 * 1000));
    const today = getZonedParts(now);
    const gridMinutes = BARN.CLOSE_MINUTE - BARN.OPEN_MINUTE;

    const days:Array<CalendarDay> = [];

    for(let offset = 0; offset < 7; offset++) {
      const date = addDays(this.weekStart, offset);
      const dayOfWeek = getDayOfWeek(date);

      const dayStart = toInstant(date.year, date.month, date.day, BARN.OPEN_MINUTE);
      const dayEnd = toInstant(date.year, date.month, date.day, BARN.CLOSE_MINUTE);

      const dayBookings = this.bookings.filter(b =>
        new Date(b.startAt).getTime() < dayEnd.getTime() && new Date(b.endAt).getTime() > dayStart.getTime());

      const slots:Array<CalendarSlot> = this.gridSlotMinutes.map(startMinute => {
        const startAt = toInstant(date.year, date.month, date.day, startMinute);
        const isCovered = dayBookings.some(b =>
          new Date(b.startAt).getTime() <= startAt.getTime() && new Date(b.endAt).getTime() > startAt.getTime());

        return {
          startMinute: startMinute,
          startAt: startAt,
          isBookable: !isCovered
            && startAt.getTime() > now.getTime()
            && startAt.getTime() <= latestBookable.getTime()
        };
      });

      const events:Array<CalendarEvent> = [];
      for(const booking of dayBookings) {
        const startMinute = Math.max(this.getMinuteOnDay(booking.startAt, date, BARN.OPEN_MINUTE), BARN.OPEN_MINUTE);
        const endMinute = Math.min(this.getMinuteOnDay(booking.endAt, date, BARN.CLOSE_MINUTE), BARN.CLOSE_MINUTE);
        if(endMinute <= startMinute) continue;

        const isBlackout = booking.kind === BARN_BOOKING_KIND.BLACKOUT;

        events.push({
          booking: booking,
          topPercent: ((startMinute - BARN.OPEN_MINUTE) / gridMinutes) * 100,
          heightPercent: ((endMinute - startMinute) / gridMinutes) * 100,
          title: this.buildEventTitle(booking),
          timeRange: `${formatTimeOfDay(startMinute)} - ${formatTimeOfDay(endMinute)}`,
          isBlackout: isBlackout,
          isMine: !isBlackout && Number(booking.bookerPlayerId) === this.currentPlayerId
        });
      }

      days.push({
        date: date,
        dayOfWeek: dayOfWeek,
        dayName: DAY_NAMES[dayOfWeek],
        dayNumber: date.day,
        monthName: MONTH_NAMES[date.month - 1],
        isToday: date.year === today.year && date.month === today.month && date.day === today.day,
        slots: slots,
        events: events,
        openSlotCount: slots.filter(s => s.isBookable).length
      });
    }

    this.days = days;
  }

  /* Minutes from midnight for an instant, or the fallback when it lands on another day. */
  private getMinuteOnDay(isoValue:string, date:BarnDate, fallback:number):number {
    const instant = new Date(isoValue);
    const parts = getZonedParts(instant);

    if(parts.year !== date.year || parts.month !== date.month || parts.day !== date.day) {
      return fallback;
    }

    return (parts.hour * 60) + parts.minute;
  }

  private buildEventTitle(booking:BarnBooking):string {
    if(booking.kind === BARN_BOOKING_KIND.BLACKOUT) {
      return booking.label || 'Barn reserved';
    }

    const bookerName = booking.bookerFirstName
      ? `${booking.bookerFirstName} ${(booking.bookerLastName ?? '').substring(0, 1)}.`
      : 'Reserved';

    const others = Math.max(booking.players.length - 1, 0);
    return others > 0 ? `${bookerName} + ${others}` : bookerName;
  }

  /* ---------- navigation ---------- */

  get visibleDays():Array<CalendarDay> {
    if(!this.isNarrowScreen) return this.days;
    const day = this.days[this.selectedDayIndex];
    return day ? [day] : [];
  }

  get weekLabel():string {
    if(this.days.length < 7) return '';
    const first = this.days[0];
    const last = this.days[6];

    return first.monthName === last.monthName
      ? `${first.monthName} ${first.dayNumber} - ${last.dayNumber}`
      : `${first.monthName} ${first.dayNumber} - ${last.monthName} ${last.dayNumber}`;
  }

  async changeWeek(offsetInWeeks:number):Promise<void> {
    this.weekStart = addDays(this.weekStart, offsetInWeeks * 7);
    this.applicationService.showGlobalSpinner();
    await this.loadWeek();
    this.applicationService.hideGlobalSpinner();
  }

  async goToThisWeek():Promise<void> {
    this.weekStart = getWeekStartDate(new Date());
    this.selectedDayIndex = this.getTodayIndexInWeek();
    this.applicationService.showGlobalSpinner();
    await this.loadWeek();
    this.applicationService.hideGlobalSpinner();
  }

  selectDay(index:number):void {
    this.selectedDayIndex = index;
  }

  private getTodayIndexInWeek():number {
    const today = getZonedParts(new Date());
    return today.dayOfWeek;
  }

  isHourStart(minute:number):boolean {
    return minute % 60 === 0;
  }

  formatMinute(minute:number):string {
    return formatTimeOfDay(minute);
  }

  /* ---------- reserving ---------- */

  openReserveModal(day:CalendarDay, slot:CalendarSlot):void {
    if(!slot.isBookable) return;

    this.reserveDay = day;
    this.reserveSlot = slot;
    this.reserveSelectedPlayerIds = [];
    this.reserveGuestNames = [];
    this.reserveDurationOptions = this.buildDurationOptions(day, slot);
    this.reserveDuration = this.reserveDurationOptions.indexOf(BARN.DEFAULT_DURATION_MINUTES) >= 0
      ? BARN.DEFAULT_DURATION_MINUTES
      : this.reserveDurationOptions[this.reserveDurationOptions.length - 1];
    this.modalError = '';
    this.activeModal = 'RESERVE';
  }

  /* A reservation can only run until closing time or the next thing already on the books. */
  private buildDurationOptions(day:CalendarDay, slot:CalendarSlot):Array<number> {
    let limit = BARN.CLOSE_MINUTE - slot.startMinute;

    for(const event of day.events) {
      const eventStartMinute = this.getMinuteOnDay(event.booking.startAt, day.date, BARN.CLOSE_MINUTE);
      if(eventStartMinute > slot.startMinute) {
        limit = Math.min(limit, eventStartMinute - slot.startMinute);
      }
    }

    limit = Math.min(limit, BARN.MAX_DURATION_MINUTES);

    const options:Array<number> = [];
    for(let minutes = BARN.MIN_DURATION_MINUTES; minutes <= limit; minutes += BARN.SLOT_MINUTES) {
      options.push(minutes);
    }

    return options.length ? options : [BARN.MIN_DURATION_MINUTES];
  }

  get reserveHeading():string {
    if(!this.reserveDay || !this.reserveSlot) return '';
    return `${this.reserveDay.dayName}, ${this.reserveDay.monthName} ${this.reserveDay.dayNumber}`;
  }

  get reserveTimeRange():string {
    if(!this.reserveSlot) return '';
    const start = this.reserveSlot.startMinute;
    return `${formatTimeOfDay(start)} - ${formatTimeOfDay(start + this.reserveDuration)}`;
  }

  get selectableRoster():Array<Player> {
    return this.roster.filter(p => p.id !== this.currentPlayerId);
  }

  isPlayerSelected(playerId:number):boolean {
    return this.reserveSelectedPlayerIds.indexOf(playerId) >= 0;
  }

  togglePlayer(playerId:number, isChecked:boolean):void {
    const index = this.reserveSelectedPlayerIds.indexOf(playerId);
    if(isChecked && index < 0) this.reserveSelectedPlayerIds.push(playerId);
    else if(!isChecked && index >= 0) this.reserveSelectedPlayerIds.splice(index, 1);
  }

  addGuest():void {
    this.reserveGuestNames.push('');
  }

  removeGuest(index:number):void {
    this.reserveGuestNames.splice(index, 1);
  }

  trackByIndex(index:number):number {
    return index;
  }

  formatDuration(minutes:number):string {
    if(minutes < 60) return `${minutes} min`;
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  async saveReservation():Promise<void> {
    if(!this.reserveDay || !this.reserveSlot) return;

    const date = this.reserveDay.date;
    const startMinute = this.reserveSlot.startMinute;

    const request = {
      bookerPlayerId: this.currentPlayerId,
      startAt: toInstant(date.year, date.month, date.day, startMinute).toISOString(),
      endAt: toInstant(date.year, date.month, date.day, startMinute + this.reserveDuration).toISOString(),
      playerIds: [...this.reserveSelectedPlayerIds],
      guestNames: this.reserveGuestNames.map(n => n.trim()).filter(n => n.length > 0)
    };

    this.modalError = '';
    this.applicationService.showGlobalSpinner();

    try {
      await this.barnService.createBooking(request);
      this.closeModal();
      this.noticeMessage = 'Your tee time is booked.';
      await this.loadWeek();
    }
    catch (err) {
      this.modalError = this.describeError(err);
    }
    finally {
      this.applicationService.hideGlobalSpinner();
    }
  }

  /* ---------- details and cancelling ---------- */

  openDetailsModal(event:CalendarEvent):void {
    this.detailsEvent = event;
    this.modalError = '';
    this.activeModal = 'DETAILS';
  }

  get detailsPlayerNames():string {
    if(!this.detailsEvent) return '';
    return this.detailsEvent.booking.players.map(p => p.name).join(', ');
  }

  async cancelBooking(deleteSeries:boolean):Promise<void> {
    if(!this.detailsEvent) return;

    const booking = this.detailsEvent.booking;
    this.modalError = '';
    this.applicationService.showGlobalSpinner();

    try {
      if(booking.kind === BARN_BOOKING_KIND.BLACKOUT) {
        await this.barnService.cancelBlackout(booking.id, booking.blackoutRuleId, deleteSeries);
        this.noticeMessage = deleteSeries ? 'The repeating block was removed.' : 'That block was removed.';
      }
      else {
        await this.barnService.cancelBooking(booking.id);
        this.noticeMessage = 'That tee time was cancelled.';
      }

      this.closeModal();
      await this.loadWeek();
      if(this.activeView === 'upcoming') await this.loadUpcomingBookings();
    }
    catch (err) {
      this.modalError = this.describeError(err);
    }
    finally {
      this.applicationService.hideGlobalSpinner();
    }
  }

  /* ---------- admin menu ---------- */

  toggleAdminMenu(event:MouseEvent):void {
    event.stopPropagation();
    this.adminMenuOpen = !this.adminMenuOpen;
  }

  onAdminMenuAction(action:'blackout'|'notifications'|'upcoming'):void {
    this.adminMenuOpen = false;
    if(action === 'blackout') this.openBlackoutModal();
    else if(action === 'notifications') this.openNotificationsModal();
    else this.openUpcomingBookings();
  }

  /* ---------- upcoming bookings ---------- */

  async openUpcomingBookings():Promise<void> {
    this.activeView = 'upcoming';
    this.applicationService.showGlobalSpinner();
    await this.loadUpcomingBookings();
    this.applicationService.hideGlobalSpinner();
  }

  showCalendarView():void {
    this.activeView = 'calendar';
    this.upcomingError = '';
  }

  async loadUpcomingBookings():Promise<void> {
    this.upcomingError = '';
    const now = new Date();
    const rangeEnd = new Date(now.getTime() + (BARN.MAX_DAYS_AHEAD * 24 * 60 * 60 * 1000));

    try {
      const bookings = await this.barnService.getAvailability(now, rangeEnd);
      this.upcomingBookings = bookings
        .filter(b => b.kind === BARN_BOOKING_KIND.RESERVATION)
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    }
    catch (err) {
      this.upcomingBookings = [];
      this.upcomingError = this.describeError(err);
    }
  }

  openUpcomingDetails(booking:BarnBooking):void {
    const startMinute = getMinutesFromMidnight(new Date(booking.startAt));
    const endMinute = getMinutesFromMidnight(new Date(booking.endAt));

    this.openDetailsModal({
      booking: booking,
      topPercent: 0,
      heightPercent: 0,
      title: this.buildEventTitle(booking),
      timeRange: `${formatTimeOfDay(startMinute)} - ${formatTimeOfDay(endMinute)}`,
      isBlackout: false,
      isMine: Number(booking.bookerPlayerId) === this.currentPlayerId
    });
  }

  formatUpcomingDate(isoValue:string):string {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: BARN.TIME_ZONE,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    }).format(new Date(isoValue));
  }

  formatUpcomingTime(booking:BarnBooking):string {
    const startMinute = getMinutesFromMidnight(new Date(booking.startAt));
    const endMinute = getMinutesFromMidnight(new Date(booking.endAt));
    return `${formatTimeOfDay(startMinute)} – ${formatTimeOfDay(endMinute)}`;
  }

  upcomingPlayers(booking:BarnBooking):string {
    return booking.players.map(p => p.name).join(', ');
  }

  /* ---------- notifications ---------- */

  get notificationRoster():Array<Player> {
    return this.roster
      .filter(p => !!p.email && p.email.trim().length > 0)
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }

  async openNotificationsModal():Promise<void> {
    this.modalError = '';
    this.notificationSelectedPlayerIds = [];
    this.activeModal = 'NOTIFICATIONS';
    this.applicationService.showGlobalSpinner();

    try {
      this.notificationSelectedPlayerIds = await this.barnService.getNotificationRecipients();
    }
    catch (err) {
      this.modalError = this.describeError(err);
    }
    finally {
      this.applicationService.hideGlobalSpinner();
    }
  }

  isNotificationRecipient(playerId:number):boolean {
    return this.notificationSelectedPlayerIds.indexOf(playerId) >= 0;
  }

  toggleNotificationRecipient(playerId:number, checked:boolean):void {
    const index = this.notificationSelectedPlayerIds.indexOf(playerId);
    if(checked && index < 0) this.notificationSelectedPlayerIds.push(playerId);
    else if(!checked && index >= 0) this.notificationSelectedPlayerIds.splice(index, 1);
  }

  async saveNotifications():Promise<void> {
    this.modalError = '';
    this.applicationService.showGlobalSpinner();

    try {
      this.notificationSelectedPlayerIds = await this.barnService.saveNotificationRecipients(
        [...this.notificationSelectedPlayerIds]
      );
      this.closeModal();
      this.noticeMessage = 'Booking notification list saved.';
    }
    catch (err) {
      this.modalError = this.describeError(err);
    }
    finally {
      this.applicationService.hideGlobalSpinner();
    }
  }

  /* ---------- blackouts ---------- */

  openBlackoutModal():void {
    /* Default to whatever day the admin is looking at, or today when the current week is up */
    const todayIndex = this.days.findIndex(d => d.isToday);
    const preferredIndex = this.isNarrowScreen ? this.selectedDayIndex : Math.max(todayIndex, 0);
    const day = this.days[preferredIndex] ?? this.days[0];
    const date = day ? day.date : getZonedParts(new Date());

    this.blackoutIsRecurring = false;
    this.blackoutLabel = '';
    this.blackoutDate = toDateLiteral(date);
    this.blackoutStartsOn = toDateLiteral(date);
    this.blackoutEndsOn = '';
    this.blackoutStartMinute = BARN.OPEN_MINUTE;
    this.blackoutEndMinute = BARN.CLOSE_MINUTE;
    this.blackoutDaysOfWeek = [];
    this.modalError = '';
    this.activeModal = 'BLACKOUT';
  }

  isBlackoutDaySelected(dayOfWeek:number):boolean {
    return this.blackoutDaysOfWeek.indexOf(dayOfWeek) >= 0;
  }

  toggleBlackoutDay(dayOfWeek:number):void {
    const index = this.blackoutDaysOfWeek.indexOf(dayOfWeek);
    if(index >= 0) this.blackoutDaysOfWeek.splice(index, 1);
    else this.blackoutDaysOfWeek.push(dayOfWeek);
  }

  async saveBlackout():Promise<void> {
    if(this.blackoutEndMinute <= this.blackoutStartMinute) {
      this.modalError = 'The block has to end after it starts.';
      return;
    }

    let request:any = {
      playerId: this.currentPlayerId,
      label: this.blackoutLabel.trim() || null
    };

    if(this.blackoutIsRecurring) {
      if(this.blackoutDaysOfWeek.length === 0) {
        this.modalError = 'Pick at least one day of the week.';
        return;
      }

      request = {
        ...request,
        daysOfWeek: [...this.blackoutDaysOfWeek],
        startTime: toTimeLiteral(this.blackoutStartMinute),
        endTime: toTimeLiteral(this.blackoutEndMinute),
        startsOn: this.blackoutStartsOn,
        endsOn: this.blackoutEndsOn || null
      };
    }
    else {
      if(!this.blackoutDate) {
        this.modalError = 'Pick a date to block.';
        return;
      }

      const date = parseDateLiteral(this.blackoutDate);
      request = {
        ...request,
        startAt: toInstant(date.year, date.month, date.day, this.blackoutStartMinute).toISOString(),
        endAt: toInstant(date.year, date.month, date.day, this.blackoutEndMinute).toISOString()
      };
    }

    this.modalError = '';
    this.applicationService.showGlobalSpinner();

    try {
      const result = await this.barnService.createBlackout(request);
      this.closeModal();

      this.noticeMessage = result.skippedCount > 0
        ? `Blocked ${result.createdCount} times. ${result.skippedCount} were skipped because they were already booked.`
        : `Blocked ${result.createdCount} ${result.createdCount === 1 ? 'time' : 'times'}.`;

      await this.loadWeek();
    }
    catch (err) {
      this.modalError = this.describeError(err);
    }
    finally {
      this.applicationService.hideGlobalSpinner();
    }
  }

  /* ---------- shared ---------- */

  closeModal():void {
    this.activeModal = '';
    this.modalError = '';
    this.reserveDay = null;
    this.reserveSlot = null;
    this.detailsEvent = null;
  }

  dismissNotice():void {
    this.noticeMessage = '';
  }

  private describeError(err:any):string {
    return err?.error?.message ?? 'Something went wrong. Please try again.';
  }
}
